/**
 * Analytical Tree - Node Details Panel
 * Shows selected node fields and anomalies.
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import { Info, Warning, Error as ErrorIcon } from '@mui/icons-material';

const NodeDetailsPanel = ({ node }) => {
  if (!node) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          Select a node to view details
        </Typography>
      </Paper>
    );
  }

  const summary = node.summary || {};
  const anomalies = node.anomalies || [];
  const severityIcon = (s) => {
    if (s === 'critical') return <ErrorIcon fontSize="small" color="error" />;
    if (s === 'warning') return <Warning fontSize="small" sx={{ color: 'warning.main' }} />;
    return <Info fontSize="small" color="info" />;
  };

  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {node.label || node.id}
      </Typography>
      <Chip label={node.type} size="small" sx={{ mb: 1 }} />

      <Typography variant="overline" color="text.secondary">Summary</Typography>
      <List dense disablePadding>
        {Object.entries(summary).map(([key, value]) => {
          if (value == null || value === '') return null;
          return (
            <ListItem key={key} disablePadding sx={{ py: 0.25 }}>
              <ListItemText
                primary={String(value)}
                secondary={key}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          );
        })}
      </List>

      {anomalies.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" color="text.secondary">Anomalies ({anomalies.length})</Typography>
          <List dense disablePadding>
            {anomalies.map((a, i) => (
              <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {severityIcon(a.severity)}
                  <ListItemText
                    primary={a.message}
                    secondary={a.code}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Paper>
  );
};

export default NodeDetailsPanel;
